package com.vamvisions.main.pages.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import com.vamvisions.main.components.ConnectCard
import com.vamvisions.main.components.background.LocalIntroState
import com.vamvisions.main.core.presentation.i18n.Strings
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.layout.PageChrome
import com.vamvisions.main.layout.PageLayout
import com.vamvisions.main.navigation.AppLink
import com.vamvisions.main.portfolio.presentation.common.yearsOfExperience
import com.vamvisions.main.theme.AppStyleSheet
import com.vamvisions.main.theme.prefersReducedMotion
import com.vamvisions.main.theme.revealAllowed
import kotlin.js.Date
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** The intro video lasts 4.2 s. The reveal starts when it ends, or at the latest 3 s after it should have ended. */
private const val REVEAL_LATEST_START_MS = 7_200L

/** When the years of experience start counting and how long each step lasts, from the Figma keyframes. */
private const val COUNT_START_MS = 664L
private const val COUNT_STEP_MS = 114L

/** The first card appears 0.35 s into the reveal, the next ones 150 ms apart. */
private const val FIRST_CARD_DELAY_MS = 350
private const val CARD_STAGGER_MS = 150

/** [HIDDEN] waits for the intro to finish, [PLAYING] runs the reveal once, [SHOWN] is the final, static state. */
private enum class RevealPhase { HIDDEN, PLAYING, SHOWN }

@Composable
fun HomeRoot(chrome: PageChrome) {
    PageLayout(title = LocalStrings.current.homeTitle, chrome = chrome, panel = false) {
        HomeScreen(onNavigate = chrome.onNavigate, onNavigateToSection = chrome.onNavigateToSection)
    }
}

/** Static content: the Home page requests nothing from the server. */
@Composable
fun HomeScreen(onNavigate: (Route) -> Unit, onNavigateToSection: (Route, String) -> Unit) {
    val strings = LocalStrings.current
    val phase = rememberRevealPhase()
    Div(attrs = {
        classes(HomeStyles.hero)
        when (phase) {
            RevealPhase.HIDDEN -> classes(HomeStyles.revealPending)
            RevealPhase.PLAYING -> classes(HomeStyles.revealPlaying)
            RevealPhase.SHOWN -> Unit
        }
    }) {
        Div(attrs = { classes(HomeStyles.greeting, HomeStyles.revealItem) }) {
            H1(attrs = { classes(HomeStyles.title) }) { Text(strings.introTitle) }
            P(attrs = { classes(HomeStyles.subtitle) }) { Text(strings.introSubtitle) }
        }
        Highlights(strings, phase, onNavigateToSection)
        ConnectCard(onNavigate, attrs = { classes(HomeStyles.connect, HomeStyles.revealItem) })
    }
}

@Composable
private fun Highlights(strings: Strings, phase: RevealPhase, onNavigateToSection: (Route, String) -> Unit) {
    val years = remember { Date().let { yearsOfExperience(it.getFullYear(), it.getMonth() + 1) } }
    val count = rememberCount(years, phase)
    Ul(attrs = { classes(HomeStyles.highlights) }) {
        HighlightCard(0, "💼", "experience", strings.highlightExperienceAccent, strings.highlightExperienceText, onNavigateToSection) {
            // The animated number is hidden from assistive technology, which reads the static title instead.
            Span(attrs = { attr("aria-hidden", "true") }) {
                Span(attrs = { classes(HomeStyles.highlightNumber) }) {
                    Span(attrs = { classes(HomeStyles.countDigit) }) { Text(count.toString()) }
                    Span(attrs = { classes(HomeStyles.plus) }) { Text("+") }
                }
                Text(" ${strings.highlightExperienceUnit}")
            }
            Span(attrs = { classes(AppStyleSheet.srOnly) }) { Text(strings.highlightExperienceTitle(years)) }
        }
        HighlightCard(1, "🧑‍💻", "pl-coding", strings.highlightTechnicalAccent, strings.highlightTechnicalText, onNavigateToSection) {
            Text(strings.highlightTechnicalTitle)
        }
        HighlightCard(2, "📈", "monetise", strings.highlightBusinessAccent, strings.highlightBusinessText, onNavigateToSection) {
            Text(strings.highlightBusinessTitle)
        }
    }
}

/** A glass card that links to the Portfolio section [fragment]. The icon is decorative. */
@Composable
private fun HighlightCard(
    index: Int,
    icon: String,
    fragment: String,
    accent: String,
    text: String,
    onNavigateToSection: (Route, String) -> Unit,
    title: @Composable () -> Unit,
) {
    Li(attrs = { classes(HomeStyles.revealItem) }) {
        AppLink(
            route = Route.Portfolio,
            onNavigate = { route -> onNavigateToSection(route, fragment) },
            fragment = fragment,
            attrs = {
                classes(AppStyleSheet.card, AppStyleSheet.glass, HomeStyles.highlight)
                style { property("animation-delay", "${FIRST_CARD_DELAY_MS + index * CARD_STAGGER_MS}ms") }
            },
        ) {
            Span(attrs = {
                classes(HomeStyles.highlightIcon)
                attr("aria-hidden", "true")
            }) { Text(icon) }
            H2(attrs = { classes(HomeStyles.highlightTitle) }) { title() }
            P(attrs = { classes(HomeStyles.highlightAccent) }) { Text(accent) }
            P(attrs = { classes(HomeStyles.highlightText) }) { Text(text) }
        }
    }
}

/**
 * The reveal plays once per visit, after the intro background has finished, and at the latest
 * [REVEAL_LATEST_START_MS] after Home is shown. It is skipped with reduced motion, or when it has already started
 * once, so going back to Home shows everything at once.
 */
@Composable
private fun rememberRevealPhase(): RevealPhase {
    val intro = LocalIntroState.current
    val reducedMotion by prefersReducedMotion()
    var phase by remember {
        mutableStateOf(if (intro.homeRevealPlayed || !revealAllowed(reducedMotion)) RevealPhase.SHOWN else RevealPhase.HIDDEN)
    }
    LaunchedEffect(reducedMotion) {
        if (reducedMotion) phase = RevealPhase.SHOWN
    }
    LaunchedEffect(Unit) {
        if (phase == RevealPhase.HIDDEN) {
            withTimeoutOrNull(REVEAL_LATEST_START_MS) { snapshotFlow { intro.introFinished }.first { it } }
            if (phase == RevealPhase.HIDDEN) phase = RevealPhase.PLAYING
        }
        intro.homeRevealPlayed = true
    }
    return phase
}

/** The number on the first card: it counts from 0 to [years] while the reveal plays, and is [years] otherwise. */
@Composable
private fun rememberCount(years: Int, phase: RevealPhase): Int {
    var count by remember { mutableIntStateOf(if (phase == RevealPhase.SHOWN) years else 0) }
    LaunchedEffect(phase) {
        when (phase) {
            RevealPhase.HIDDEN -> count = 0
            RevealPhase.PLAYING -> {
                count = 0
                delay(COUNT_START_MS)
                for (value in 1..years) {
                    count = value
                    if (value < years) delay(COUNT_STEP_MS)
                }
            }
            RevealPhase.SHOWN -> count = years
        }
    }
    return count
}
